import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-plugin-dragdata';
import { HuePicker } from 'react-color';
import { db } from '../firebase';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, where } from 'firebase/firestore';

Chart.register(...registerables);

function PentagonChart() {
    const chartRef = useRef();
    const colorPickerRef = useRef();
    const currentIndexRef = useRef(null);
    const [hue, setHue] = useState(120);
    
    // Estado para sistema de guardado
    const [activeTab, setActiveTab] = useState('create');
    const [savedCharts, setSavedCharts] = useState([]);
    const [currentChartId, setCurrentChartId] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);

    const [data, setData] = useState({
        labels: ['1', '2', '3', '4', '5'],
        datasets: [{
            label: 'Title',
            data: [100, 100, 100, 100, 100],
            fill: true,
            backgroundColor: `hsla(${hue}, 100%, 50%, 0.2)`,
            borderColor: `hsl(${hue}, 100%, 50%)`,
            pointBackgroundColor: `hsl(${hue}, 100%, 50%)`,
            pointBorderColor: `hsl(${hue}, 100%, 50%)`,
            pointHoverBackgroundColor: `hsl(${hue}, 100%, 50%)`,
            pointHoverBorderColor: `hsl(${hue}, 100%, 50%)`,
            pointRadius: 3,
            pointHoverRadius: 5,
            pointHitRadius: 12,
        }],
    });

    const [editedLabels, setEditedLabels] = useState([...data.labels]);
    const [editedData, setEditedData] = useState([...data.datasets[0].data]);
    const [chartTitle, setChartTitle] = useState(data.datasets[0].label);
    const chartInstance = useRef(null);

    // Verificar usuario logueado al montar y cuando cambie
    useEffect(() => {
        const checkUser = () => {
            const user = localStorage.getItem('currentUser');
            if (user) {
                setCurrentUser(JSON.parse(user));
            } else {
                setCurrentUser(null);
            }
        };

        checkUser();
        
        // Listener para cambios en localStorage (logout desde otro componente)
        window.addEventListener('storage', checkUser);
        
        return () => window.removeEventListener('storage', checkUser);
    }, []);

// Cargar gráficos desde Firestore
useEffect(() => {
    const loadCharts = async () => {
        if (currentUser && currentUser.uid) {
            try {
                const q = query(
                    collection(db, 'charts'), 
                    where('userId', '==', currentUser.uid)
                );
                const querySnapshot = await getDocs(q);
                const charts = [];
                querySnapshot.forEach((doc) => {
                    charts.push({ id: doc.id, ...doc.data() });
                });
                setSavedCharts(charts);
            } catch (error) {
                console.error('Error loading charts:', error);
                setSavedCharts([]);
            }
        } else {
            setSavedCharts([]);
        }
    };

    loadCharts();
}, [currentUser]);


    const handleColorPickerClick = (event) => {
        const picker = event.target;
        const rect = picker.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const hue = (x / rect.width) * 360;
        setHue(hue);
        setData(prevData => ({
            ...prevData,
            datasets: [{
                ...prevData.datasets[0],
                backgroundColor: `hsla(${hue}, 100%, 50%, 0.2)`,
                borderColor: `hsl(${hue}, 100%, 50%)`,
                pointBackgroundColor: `hsl(${hue}, 100%, 50%)`,
                pointBorderColor: `hsl(${hue}, 100%, 50%)`,
                pointHoverBackgroundColor: `hsl(${hue}, 100%, 50%)`,
                pointHoverBorderColor: `hsl(${hue}, 100%, 50%)`,
            }]
        }));
    };

    const createPentagonChart = useCallback((chartData) => {
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        if (!chartRef.current) return;

        const ctx = chartRef.current.getContext('2d');
        chartInstance.current = new Chart(ctx, {
            type: 'radar',
            data: chartData,
            options: {
                elements: {
                    line: { borderWidth: 3 },
                },
                scales: {
                    r: {
                        min: 0,
                        max: 100,
                        ticks: { display: false },
                        grid: { color: 'grey' },
                        angleLines: { color: '#fff' },
                        pointLabels: {
                            color: 'White',
                            font: { size: 10 }
                        }
                    },
                },
                plugins: {
                    dragData: {
                        onDragStart: function(e, datasetIndex, index, value) {
                            currentIndexRef.current = index;
                            chartInstance.current.options.plugins.tooltip.callbacks.label = function(context) {
                                const initialValue = chartInstance.current.data.datasets[datasetIndex].data[index];
                                return chartInstance.current.data.labels[currentIndexRef.current] + ': ' + Math.round(initialValue);
                            };
                            chartInstance.current.options.plugins.tooltip.enabled = true;
                            chartInstance.current.update();
                        },
                        onDrag: function(e, datasetIndex, index, value) {
                            chartInstance.current.options.plugins.tooltip.callbacks.label = function(context) {
                                return chartInstance.current.data.labels[currentIndexRef.current] + ': ' + Math.round(value);
                            };
                            chartInstance.current.update('none');
                        },
                        onDragEnd: function(e, datasetIndex, index, value) {
                            setEditedData(prevData => {
                                const newData = [...prevData];
                                newData[index] = Math.round(value);
                                return newData;
                            });
                            chartInstance.current.options.plugins.tooltip.enabled = false;
                            chartInstance.current.update();
                        },
                        showTooltip: true,
                        magnet: {
                            to: (value) => Math.max(0, value)
                        }
                    },
                    tooltip: {
                        enabled: false,
                        mode: 'index',
                        intersect: false,
                        callbacks: {
                            title: function() { return ''; },
                            label: function(context) {
                                return context.label + ': ' + context.formattedValue;
                            },
                        }
                    },
                    title: {
                        display: true,
                        text: chartTitle,
                        color: `hsl(${hue}, 100%, 50%)`,
                        font: { size: 20 }
                    },
                    legend: { display: false },
                },
                maintainAspectRatio: false,
                aspectRatio: 1,
            },
        });
    }, [chartTitle, hue]);

    useEffect(() => {
        if (chartRef.current) {
            createPentagonChart(data);
        }
    }, [data, createPentagonChart]);

    useEffect(() => {
        if (activeTab === 'create' && chartRef.current) {
            setTimeout(() => {
                createPentagonChart(data);
            }, 50);
        }
    }, [activeTab, createPentagonChart, data]);

    useEffect(() => {
        const handleTouchStart = (e) => {
            e.preventDefault();
        };
        const colorPickerElem = colorPickerRef.current;
        if (colorPickerElem) {
            colorPickerElem.addEventListener('touchstart', handleTouchStart, { passive: false });
        }
        return () => {
            if (colorPickerElem) {
                colorPickerElem.removeEventListener('touchstart', handleTouchStart);
            }
        };
    }, []);

    const handleAdd = () => {
        const newLabel = `${editedLabels.length + 1}`;
        setEditedLabels([...editedLabels, newLabel]);
        setEditedData([...editedData, 100]);
        setData({
            ...data,
            labels: [...editedLabels, newLabel],
            datasets: data.datasets.map(dataset => ({
                ...dataset,
                data: [...editedData, 100],
            })),
        });
    };

    const handleRemove = () => {
        if (editedLabels.length > 3) {
            setEditedLabels(editedLabels.slice(0, -1));
            setEditedData(editedData.slice(0, -1));
            setData({
                ...data,
                labels: editedLabels.slice(0, -1),
                datasets: data.datasets.map(dataset => ({
                    ...dataset,
                    data: editedData.slice(0, -1),
                })),
            });
        }
    };

    const handleLabelChange = () => {
        setData({
            ...data,
            labels: [...editedLabels],
            datasets: data.datasets.map(dataset => ({
                ...dataset,
                data: [...editedData],
                label: chartTitle,
            })),
        });
    };

    const handleLabelEdit = (index, newValue) => {
        const newEditedLabels = [...editedLabels];
        newEditedLabels[index] = newValue;
        setEditedLabels(newEditedLabels);
    };

    const handleDataEdit = (index, event) => {
        const value = event.target.value;
        if (/^\d*\.?\d*$/.test(value) || value === '') {
            const newEditedData = [...editedData];
            if (value !== '' && parseFloat(value) <= 100) {
                newEditedData[index] = parseFloat(value);
            } else if (value === '') {
                newEditedData[index] = value;
            } else {
                return;
            }
            setEditedData(newEditedData);
        }
    };
// Función para generar preview del gráfico
const generateChartPreview = () => {
    if (chartInstance.current) {
        return chartInstance.current.toBase64Image();
    }
    return null;
};

// GUARDAR CHART - Con Firestore
const handleSaveChart = async () => {
    // Verificar login
    if (!currentUser) {
        alert('⚠️ Please login to save charts');
        return;
    }

const chartData = {
    title: chartTitle || 'Untitled Chart',
    labels: [...editedLabels],
    data: [...editedData],
    hue: hue,
    userId: currentUser.uid,
    username: currentUser.username,
    preview: generateChartPreview(), // ← AGREGAR ESTA LÍNEA
    createdAt: currentChartId ? savedCharts.find(c => c.id === currentChartId)?.createdAt : new Date().toISOString(),
    updatedAt: new Date().toISOString()
};

    try {
        if (currentChartId) {
            // UPDATE
            const chartRef = doc(db, 'charts', currentChartId);
            await updateDoc(chartRef, chartData);
            
            setSavedCharts(savedCharts.map(chart => 
                chart.id === currentChartId ? { ...chartData, id: currentChartId } : chart
            ));
            alert('✅ Chart updated successfully!');
        } else {
            // CREATE
            const docRef = await addDoc(collection(db, 'charts'), chartData);
            const newChart = { ...chartData, id: docRef.id };
            
            setSavedCharts([...savedCharts, newChart]);
            setCurrentChartId(docRef.id);
            alert('✅ Chart saved successfully!');
        }
    } catch (error) {
        console.error('Error saving chart:', error);
        alert('❌ Error saving chart: ' + error.message);
    }
};
    const handleLoadChart = (chart) => {
        setChartTitle(chart.title);
        setEditedLabels([...chart.labels]);
        setEditedData([...chart.data]);
        setHue(chart.hue);
        setCurrentChartId(chart.id);
        
        setData({
            labels: [...chart.labels],
            datasets: [{
                label: chart.title,
                data: [...chart.data],
                fill: true,
                backgroundColor: `hsla(${chart.hue}, 100%, 50%, 0.2)`,
                borderColor: `hsl(${chart.hue}, 100%, 50%)`,
                pointBackgroundColor: `hsl(${chart.hue}, 100%, 50%)`,
                pointBorderColor: `hsl(${chart.hue}, 100%, 50%)`,
                pointHoverBackgroundColor: `hsl(${chart.hue}, 100%, 50%)`,
                pointHoverBorderColor: `hsl(${chart.hue}, 100%, 50%)`,
                pointRadius: 3,
                pointHoverRadius: 5,
                pointHitRadius: 12,
            }],
        });
        
        setActiveTab('create');
    };

const handleDeleteChart = async (chartId) => {
    if (!window.confirm('Are you sure you want to delete this chart?')) return;
    
    try {
        await deleteDoc(doc(db, 'charts', chartId));
        setSavedCharts(savedCharts.filter(chart => chart.id !== chartId));
        if (currentChartId === chartId) {
            setCurrentChartId(null);
        }
        alert('✅ Chart deleted successfully!');
    } catch (error) {
        console.error('Error deleting chart:', error);
        alert('❌ Error deleting chart: ' + error.message);
    }
};

    const handleNewChart = () => {
        setChartTitle('Title');
        setEditedLabels(['1', '2', '3', '4', '5']);
        setEditedData([100, 100, 100, 100, 100]);
        setHue(120);
        setCurrentChartId(null);
        
        setData({
            labels: ['1', '2', '3', '4', '5'],
            datasets: [{
                label: 'Title',
                data: [100, 100, 100, 100, 100],
                fill: true,
                backgroundColor: `hsla(120, 100%, 50%, 0.2)`,
                borderColor: `hsl(120, 100%, 50%)`,
                pointBackgroundColor: `hsl(120, 100%, 50%)`,
                pointBorderColor: `hsl(120, 100%, 50%)`,
                pointHoverBackgroundColor: `hsl(120, 100%, 50%)`,
                pointHoverBorderColor: `hsl(120, 100%, 50%)`,
                pointRadius: 3,
                pointHoverRadius: 5,
                pointHitRadius: 12,
            }],
        });
    };

    const handleExportImage = () => {
        if (chartInstance.current) {
            const url = chartInstance.current.toBase64Image();
            const link = document.createElement('a');
            link.download = `${chartTitle.replace(/\s+/g, '_')}_radar_chart.png`;
            link.href = url;
            link.click();
        }
    };

    return (
        <div className="chart-app-container">
            {/* Mostrar mensaje si no está logueado */}
            {!currentUser && (
                <div className="login-warning">
                    <p>⚠️ Login to save your charts</p>
                </div>
            )}

            {/* Tabs */}
            <div className="chart-tabs">
                <button 
                    className={`tab-btn ${activeTab === 'create' ? 'active' : ''}`}
                    onClick={() => setActiveTab('create')}
                >
                    📊 Create Chart
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'saved' ? 'active' : ''}`}
                    onClick={() => setActiveTab('saved')}
                    disabled={!currentUser}
                    title={!currentUser ? 'Login to see saved charts' : ''}
                >
                    💾 Saved Charts ({savedCharts.length})
                </button>
            </div>

            {activeTab === 'create' ? (
                <div className="pentagon-chart-container">
                    {/* Botones de acción */}
                    <div className="action-buttons-top">
                        <button className="action-btn primary" onClick={handleSaveChart}>
                            💾 {currentChartId ? 'Update' : 'Save'} Chart
                        </button>
                        <button className="action-btn secondary" onClick={handleExportImage}>
                            📥 Export PNG
                        </button>
                        <button className="action-btn secondary" onClick={handleNewChart}>
                            ➕ New Chart
                        </button>
                    </div>

                    <div className="pentagon-chart">
                        <div className="pentagon-chart-canvas" style={{ touchAction: 'none' }}>
                            <canvas ref={chartRef} />
                        </div>
                    </div>
                    
                    <div className="color-picker-container" onClick={handleColorPickerClick}>
                        <HuePicker color={`hsl(${hue}, 100%, 50%)`} />
                    </div>
            
                    <div className="pentagon-chart-inputs">
                        <div className="title-and-button-container">
                            <div className="title-input-container">
                                <input
                                    type="text"
                                    value={chartTitle}
                                    onChange={(e) => setChartTitle(e.target.value)}
                                    onBlur={handleLabelChange}
                                    placeholder="Chart Title"
                                />
                            </div>
                            <div className="update-button-container">
                                <button onClick={handleAdd}>+</button>
                                <button onClick={handleRemove}>-</button>
                            </div>
                        </div>
                        
                        {editedLabels.map((label, index) => (
                            <div key={index} style={{ display: 'flex', justifyContent: 'center', gap: '5px' }}>
                                <input
                                    type="text"
                                    value={editedLabels[index]}
                                    maxLength="12"
                                    onChange={event => handleLabelEdit(index, event.target.value)}
                                    onBlur={handleLabelChange}
                                    placeholder="Label"
                                />
                                <input
                                    type="text"
                                    value={editedData[index]}
                                    onChange={event => handleDataEdit(index, event)}
                                    onBlur={handleLabelChange}
                                    placeholder="Value"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="saved-charts-section">
                    {!currentUser ? (
                        <div className="empty-state">
                            <h2>🔒 Login Required</h2>
                            <p>Please login to see your saved charts</p>
                        </div>
                    ) : savedCharts.length === 0 ? (
                        <div className="empty-state">
                            <h2>📊 No Saved Charts Yet</h2>
                            <p>Create your first radar chart and save it!</p>
                            <button 
                                className="action-btn primary"
                                onClick={() => setActiveTab('create')}
                            >
                                Create Your First Chart
                            </button>
                        </div>
                    ) : (
<div className="charts-grid">
    {savedCharts.map(chart => (
        <div key={chart.id} className="chart-card">
            {/* Preview del gráfico */}
            {chart.preview && (
                <div className="chart-preview">
                    <img src={chart.preview} alt={chart.title} />
                </div>
            )}
            
            <h3 className="chart-card-title">{chart.title}</h3>
            <div className="chart-card-info">
                <p><strong>Labels:</strong> {chart.labels.join(', ')}</p>
                <p><strong>Created:</strong> {new Date(chart.createdAt).toLocaleDateString()}</p>
                {chart.updatedAt && (
                    <p><strong>Updated:</strong> {new Date(chart.updatedAt).toLocaleDateString()}</p>
                )}
            </div>
                                    <div className="chart-card-actions">
                                        <button 
                                            className="card-btn load"
                                            onClick={() => handleLoadChart(chart)}
                                        >
                                            📂 Load
                                        </button>
                                        <button 
                                            className="card-btn delete"
                                            onClick={() => handleDeleteChart(chart.id)}
                                        >
                                            🗑️ Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default PentagonChart;








































